(function HotelEnginePricing($, Adzuki) {
	'use strict';

	Adzuki.HotelEngine = Adzuki.HotelEngine || {};

	const HE = Adzuki.HotelEngine;

	HE.emptyRatePrices = function emptyRatePrices() {
		return {
			prepaidFees           : 0,
			postpaidFees          : 0,
			prepaidFeeItems       : [],
			postpaidFeeItems      : [],
			hasPrepaidFees        : false,
			hasPostpaidFees       : false,
			taxesIncluded         : false,
			roomTaxes             : 0,
			sellingPrice          : 0,
			roomRate              : 0,
			totalPlusTaxes        : 0,
			totalDueToday         : 0,
			totalCost             : 0,
			totalFees             : 0,
			per_night_taxes_fees  : 0,
			per_night_plus_taxes  : 0,
			per_night_prepaid_fees: 0,
			per_night_per_room    : 0,
			per_night_total       : 0,
			totalPrice            : 0,
			marketRate            : 0,
			savings               : 0,
			agent_commission      : 0,
			agent_commission_pct  : 0,
		};
	};

	HE.getFeeAmount = function getFeeAmount(fee) {
		return parseFloat(fee?.converted_rate) || parseFloat(fee?.rate) || parseFloat(fee?.amount) || 0;
	};

	HE.getMandatoryFeeItems = function getMandatoryFeeItems(rate, type) {
		if (!rate) {
			return [];
		}

		const pricing         = rate.pricing || {};
		const providerPricing = rate.provider_pricing || {};
		const hotelPricing    = rate.hotel_pricing || {};

		if (Array.isArray(pricing?.mandatory_fees?.[type]) && pricing.mandatory_fees[type].length) {
			return pricing.mandatory_fees[type];
		}

		if (Array.isArray(providerPricing?.mandatory_fees?.[type]) && providerPricing.mandatory_fees[type].length) {
			return providerPricing.mandatory_fees[type];
		}

		if (Array.isArray(hotelPricing?.mandatory_fees?.[type]) && hotelPricing.mandatory_fees[type].length) {
			return hotelPricing.mandatory_fees[type];
		}

		if (type === 'postpaid' && Array.isArray(pricing?.property_fees) && pricing.property_fees.length) {
			return pricing.property_fees;
		}

		return [];
	};

	HE.sumMandatoryFees = function sumMandatoryFees(rate, type) {
		return HE.getMandatoryFeeItems(rate, type).reduce(
			(sum, fee) => sum + HE.getFeeAmount(fee),
			0
		);
	};

	HE.taxesIncludedInSellingPrice = function taxesIncludedInSellingPrice(pricing) {
		return Boolean(pricing?.selling_price_includes_tax_and_fees);
	};

	/**
	 * Taxes and fees that still have to be added on top of the selling price.
	 * Returns 0 for rates that already have them rolled into selling_price.
	 */
	HE.getAdditiveTaxesAndFees = function getAdditiveTaxesAndFees(pricing) {
		if (!pricing || HE.taxesIncludedInSellingPrice(pricing)) {
			return 0;
		}

		const taxesFees = parseFloat(pricing.taxes_fees) || parseFloat(pricing.selling_taxes) || 0;

		if (taxesFees > 0) {
			return taxesFees;
		}

		// navitrip pricing quotes the tax and the platform fee separately
		const splitTaxes = (parseFloat(pricing.taxes) || 0) + (parseFloat(pricing.platform_fee) || 0);

		if (splitTaxes > 0) {
			return splitTaxes;
		}

		const sellingPrice = parseFloat(pricing.selling_price) || 0;
		const totalPrice   = parseFloat(pricing.total_price) || 0;

		return totalPrice > sellingPrice ? totalPrice - sellingPrice : 0;
	};

	/**
	 * The tax and fee amount to display. When selling_price already includes tax
	 * and fees, taxes_fees is not the amount that was rolled in (it is calculated
	 * off a different base), so derive the included portion from the pre-tax price.
	 */
	HE.getTaxesAndFees = function getTaxesAndFees(pricing) {
		if (!pricing) {
			return 0;
		}

		if (!HE.taxesIncludedInSellingPrice(pricing)) {
			return HE.getAdditiveTaxesAndFees(pricing);
		}

		const sellingPrice = parseFloat(pricing.selling_price) || 0;
		const preTaxPrice  = parseFloat(pricing.selling_price_pre_tax_and_fees) || 0;

		if (sellingPrice > 0 && preTaxPrice > 0) {
			return sellingPrice - preTaxPrice;
		}

		return (parseFloat(pricing.taxes) || 0) + (parseFloat(pricing.platform_fee) || 0);
	};

	/**
	 * Pre-tax room rate. Tax inclusive rates quote selling_price with the taxes
	 * already rolled in, so strip them back out; otherwise the displayed room rate
	 * plus taxes and fees does not add up to the total due today.
	 */
	HE.getRoomRate = function getRoomRate(pricing) {
		if (!pricing) {
			return 0;
		}

		const sellingPrice = parseFloat(pricing.selling_price) || 0;

		if (!HE.taxesIncludedInSellingPrice(pricing)) {
			return sellingPrice;
		}

		const preTaxPrice = parseFloat(pricing.selling_price_pre_tax_and_fees) || 0;

		if (preTaxPrice > 0) {
			return preTaxPrice;
		}

		const includedTaxes = HE.getTaxesAndFees(pricing);

		return sellingPrice > includedTaxes ? sellingPrice - includedTaxes : sellingPrice;
	};

	HE.computeDueTodayFromPricing = function computeDueTodayFromPricing(
		rate,
		pricing,
		markup = 0,
		additionalSavings = 0
	) {
		if (!pricing) {
			return 0;
		}

		const sellingPrice = parseFloat(pricing.selling_price) || 0;
		const roomTaxes    = HE.getAdditiveTaxesAndFees(pricing);
		const totalPrepaid = HE.sumMandatoryFees(rate, 'prepaid');
		const dueToday     = sellingPrice + roomTaxes + totalPrepaid + markup - additionalSavings;

		return dueToday > 0 ? dueToday : 0;
	};

	/**
	 * Compute price breakdown for a single rate/room object that has `.pricing`.
	 *
	 * @param {Object} rate
	 * @param {Object} [options]
	 * @param {number} [options.numNights=1]
	 * @param {number} [options.numRooms=1]
	 * @param {number} [options.markup=0]
	 * @param {number} [options.additionalSavings=0]
	 * @returns {Object}
	 */
	HE.computeRatePrices = function computeRatePrices(rate, options = {}) {
		const pricing = rate?.pricing || rate?.hotel_pricing;

		if (!pricing) {
			return HE.emptyRatePrices();
		}

		const numNights         = options.numNights || 1;
		const numRooms          = options.numRooms || 1;
		const markup            = options.markup || 0;
		const additionalSavings = options.additionalSavings || 0;

		const prepaidFeeItems  = HE.getMandatoryFeeItems(rate, 'prepaid');
		const postpaidFeeItems = HE.getMandatoryFeeItems(rate, 'postpaid');
		const prepaidFees      = HE.sumMandatoryFees(rate, 'prepaid');
		const postpaidFees     = HE.sumMandatoryFees(rate, 'postpaid');
		const taxesIncluded    = HE.taxesIncludedInSellingPrice(pricing);
		const roomTaxes        = HE.getTaxesAndFees(pricing);
		const sellingPrice     = parseFloat(pricing.selling_price) || 0;
		const roomRate         = HE.getRoomRate(pricing);
		const totalPlusTaxes   = sellingPrice + HE.getAdditiveTaxesAndFees(pricing);
		const totalDueToday    = HE.computeDueTodayFromPricing(rate, pricing, markup, additionalSavings);
		const totalCost        = totalDueToday + postpaidFees;
		const taxesAndPrepaid  = roomTaxes + prepaidFees;

		// per-night is quoted pre-tax so it stays comparable across both rate types
		const perNightPrice    = roomRate || sellingPrice;

		return {
			prepaidFees,
			postpaidFees,
			prepaidFeeItems,
			postpaidFeeItems,
			hasPrepaidFees        : prepaidFees > 0,
			hasPostpaidFees       : postpaidFees > 0,
			taxesIncluded,
			roomTaxes,
			sellingPrice,
			roomRate,
			totalPlusTaxes,
			totalDueToday,
			totalCost,
			totalPrice            : totalDueToday,
			totalFees             : taxesAndPrepaid,
			per_night_taxes_fees  : taxesAndPrepaid ? taxesAndPrepaid / numNights / numRooms : 0,
			per_night_plus_taxes  : totalPlusTaxes ? totalPlusTaxes / numNights / numRooms : 0,
			per_night_prepaid_fees: prepaidFees ? prepaidFees / numNights / numRooms : 0,
			per_night_per_room    : perNightPrice ? perNightPrice / numNights / numRooms : 0,
			per_night_total       : totalDueToday ? totalDueToday / numNights / numRooms : 0,
			marketRate            : pricing.market_rate || 0,
			savings               : pricing.savings || 0,
			agent_commission      : parseFloat(pricing.agent_commission) || 0,
			agent_commission_pct  : parseFloat(pricing.agent_commission_sp_pct) || 0,
		};
	};

	HE.computeRatesPrices = function computeRatesPrices(rates, options = {}) {
		if (!Array.isArray(rates) || !rates.length) {
			return [];
		}

		return rates.map((rate) => HE.computeRatePrices(rate, options));
	};

}(jQuery, window.Adzuki || {}));
